import React, { useEffect, useState } from "react";
import { Card, Spin, Empty } from "antd";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import { getProfitLossBalanceApi, getSalesAmountApi } from "../../../apiservice/dashboard";
import { currentDateStartTime, currentDateEndTime } from "../../../lib/functions";
import dayjs from "dayjs";
import ChartTooltip from "./ChartTooltip";
import { SERIES } from "../dashboardTheme";

const SLICE_COLORS = {
  "Today Sale": SERIES.blue,
  "Today Expense": SERIES.red,
  "Today Profit": SERIES.aqua,
  "Today Loss": SERIES.orange,
};

const TodaySummary = ({ currency = "INR" }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = dayjs();
    const fromDate = currentDateStartTime(today);
    const toDate = currentDateEndTime(today);

    setLoading(true);
    const fetchData = async () => {
      try {
        const [profitLoss, sales] = await Promise.all([
          getProfitLossBalanceApi({ fromDate, toDate, selectedFilterType: "Date Wise", page: "dashboard" }),
          getSalesAmountApi({ fromDate, toDate, page: "dashboard" }),
        ]);

        const todaySale = parseFloat(sales.data?.[0]?.total_amount || 0);
        const todayProfit = parseFloat(profitLoss.data?.netProfit || 0);
        const todayExpense =
          parseFloat(profitLoss.data?.indirectExpenseBalance || 0) +
          parseFloat(profitLoss.data?.directExpenseBalance || 0);

        const isProfit = todayProfit >= 0;
        const rawPayload = [
          { name: "Today Sale", value: Number(todaySale.toFixed(2)) },
          { name: "Today Expense", value: Number(todayExpense.toFixed(2)) },
          { name: isProfit ? "Today Profit" : "Today Loss", value: Number(Math.abs(todayProfit).toFixed(2))},
        ];
        const payload = rawPayload.filter(p => p.value > 0);
        setData(payload);

      } catch (err) {
        console.error("Failed to fetch today summary", err);
      }finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const sale = data.find((d) => d.name === "Today Sale")?.value || 0;

  return (
    <Card className="erp-card" variant="borderless">
      <div className="erp-card-head">
        <div>
          <div className="erp-card-title">Today Summary</div>
          <div className="erp-card-sub">{dayjs().format("DD MMM YYYY")}</div>
        </div>
      </div>

      {loading ? (
        <div className="erp-loading" style={{ height: 168 }}>
          <Spin />
        </div>
      ) : !data.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available for today" className="erp-empty" />
      ) : (
        <div className="erp-donut">
          <div className="erp-donut-chart">
            <ResponsiveContainer width="100%" height={168}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={56}
                  outerRadius={80}
                  paddingAngle={3}
                  stroke="none"
                >
                  {data.map((entry) => (
                    <Cell key={entry.name} fill={SLICE_COLORS[entry.name] || "#c3c2b7"} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip currency={currency} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="erp-donut-center">
              <b title={`${sale.toFixed(2)} ${currency}`}>{sale.toFixed(2)}</b>
              <span>Sales {currency}</span>
            </div>
          </div>

          <ul className="erp-list">
            {data.map((d) => (
              <li key={d.name}>
                <i style={{ background: SLICE_COLORS[d.name] || "#c3c2b7" }} />
                <span className="name">{d.name}</span>
                <b>
                  {Number(d.value).toFixed(2)} <span className="unit">{currency}</span>
                </b>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default TodaySummary;
